import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import { SafeAreaView } from 'react-native-safe-area-context'
import styled from 'styled-components/native'
import type { NativeStackScreenProps } from '@react-navigation/native-stack'

import PatientActions from 'APP/Redux/PatientRedux'
import I18n from 'APP/I18n'
import Analytics from 'APP/Services/Analytics'
import { getEnrolmentCodeErrorText } from 'APP/Lib/AuthHelpers'
import { Images } from 'APP/Themes'
import LogoHeader from 'APP/Components/LogoHeader'
import { ResponsiveView } from 'APP/Converse/Layout'
import Button from 'APP/Converse/Button'
import TextInput from 'APP/Converse/Inputs/TextInput'
import { useDevTools } from 'APP/Lib/DevTools'
import { View } from 'react-native'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
  justify-content: space-between;
  flex: 1;
`

type ClaimAccessScreenProps = NativeStackScreenProps<
  ReactNavigation.RootParamList,
  'claimAccessScreen'
>

const ClaimAccessScreen = ({ route }: ClaimAccessScreenProps) => {
  const { code, analyticsName } = route?.params || {}
  const dispatch = useDispatch()
  const { error, requestRunning } = useSelector((state) => state.patient)
  const [enrolmentCode, setEnrolmentCode] = useState(code ?? '')
  const [showEnrolmentCodeError, setShowEnrolmentCodeError] = useState(false)
  const { setToolName, setToolFunction } = useDevTools()

  const [showAuthError, setShowAuthError] = useState(false)

  useEffect(() => {
    setToolName('Enrol')
    setToolFunction(() => () => {
      dispatch(PatientActions.patientEnrolWithEnrolmentCodesRequest(['bobettes']))
    })
    return () => {
      setToolFunction(null)
      setToolName(null)
    }
  }, [])

  const enrolmentCodeErrorText = getEnrolmentCodeErrorText(
    showEnrolmentCodeError,
    showAuthError,
    error
  )

  const enrolmentCodeRef = useRef(null)

  const handleChange = useCallback((text: string) => {
    setEnrolmentCode(text)
    setShowAuthError(false)
  }, [])

  const handleBlur = useCallback(() => {
    const isValid = enrolmentCode.length > 0
    setShowEnrolmentCodeError(!isValid)
  }, [enrolmentCode])

  const isFormValid = requestRunning || !enrolmentCodeErrorText || enrolmentCode.length > 0

  const handleSubmit = useCallback(() => {
    Analytics.trackEvent(
      'button_click',
      {
        button_text: 'Next',
        button_value: 'Submit',
      },
      ['banner_context']
    )

    setShowEnrolmentCodeError(false)
    setShowAuthError(true)

    if (isFormValid) {
      dispatch(PatientActions.patientEnrolWithEnrolmentCodesRequest([enrolmentCode.trim()]))
    }
  }, [enrolmentCode, isFormValid, dispatch])

  return (
    <ScreenContainer>
      <ContentContainer>
        <View>
          <LogoHeader
            source={Images.logoBlue}
            title={I18n.t('ClaimAccessScreen.title')}
            subtitle={I18n.t('ClaimAccessScreen.subtitle')}
          />
          <TextInput
            id="access-code"
            keyboardType="default"
            ref={enrolmentCodeRef}
            testID="ACCESS_CODE_FIELD"
            onChangeText={handleChange}
            onBlur={handleBlur}
            returnKeyType="next"
            onSubmitEditing={handleSubmit}
            autoCorrect={false}
            label={I18n.t('ClaimAccessScreen.accessCode')}
            value={enrolmentCode}
            error={enrolmentCodeErrorText || undefined}
            noBottomMargin={true}
          />
        </View>
        <View>
          <Button
            widthVariant="full"
            title={I18n.t('ClaimAccessScreen.next')}
            analyticsName={`${analyticsName}.next`}
            disabled={!isFormValid || requestRunning}
            onPress={handleSubmit}
            isLoading={requestRunning}
            testID="NEXT_BTN"
          />
        </View>
      </ContentContainer>
    </ScreenContainer>
  )
}

export default ClaimAccessScreen
